import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  FileText,
  Zap,
  PieChart,
  BarChart3,
  CalendarClock,
  Wallet,
  AlertCircle,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import api from '@/lib/api';

const FinancePipelinePage = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [aging, setAging] = useState(null);
  const [paymentTracking, setPaymentTracking] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [budgetAlerts, setBudgetAlerts] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewRes, agingRes, paymentRes, suggestionsRes, alertsRes, reconRes] = await Promise.all([
        api.get('/finance-pipeline/overview'),
        api.get('/finance-pipeline/aging?status=SUBMITTED'),
        api.get('/finance-pipeline/payment-tracking?days=90'),
        api.get('/finance-pipeline/auto-claim-suggestions'),
        api.get('/finance-pipeline/budget-alerts'),
        api.get('/finance-pipeline/reconciliation-summary?days=90')
      ]);
      
      setOverview(overviewRes.data?.data);
      setAging(agingRes.data?.data);
      setPaymentTracking(paymentRes.data?.data);
      setSuggestions(suggestionsRes.data?.data);
      setBudgetAlerts(alertsRes.data?.data);
      setReconciliation(reconRes.data?.data);
    } catch (err) {
      notify.error(err, 'FINANCE_PIPELINE_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value || 0);
  };

  const handleQuickGenerate = async (participantId) => {
    try {
      await api.post(`/finance-pipeline/quick-generate?participant_id=${participantId}`);
      toast.success('Claim generated successfully');
      fetchData();
    } catch (err) {
      notify.error(err, 'CLAIM_GENERATE_FAILED');
    }
  };

  // Pipeline Stage Card
  const PipelineStage = ({ stage, data, icon: Icon, color, isLast }) => (
    <div className="flex items-center gap-2" data-testid={`stage-${stage.toLowerCase()}`}>
      <div className={`flex-1 p-4 rounded-lg border ${color} bg-opacity-10`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5" />
          <span className="font-medium text-sm">{stage}</span>
        </div>
        <p className="text-2xl font-bold">{data?.count || 0}</p>
        <p className="text-sm text-gray-500">{formatCurrency(data?.total_value)}</p>
      </div>
      {!isLast && <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="finance-pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600" />
            Finance Pipeline
          </h1>
          <p className="text-gray-600 mt-1">
            Service &rarr; Claim &rarr; Submission &rarr; Payment &rarr; Reconciliation
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pipeline Visualization */}
      {overview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Claim Pipeline Status</CardTitle>
            <CardDescription>
              {overview.summary.total_items_in_pipeline} items worth {formatCurrency(overview.summary.total_value_in_pipeline)} in pipeline
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <PipelineStage stage="Eligible" data={overview.stages.ELIGIBLE} icon={FileText} color="border-blue-500 bg-blue-50" />
              <PipelineStage stage="Draft" data={overview.stages.DRAFT} icon={FileText} color="border-gray-500 bg-gray-50" />
              <PipelineStage stage="Ready" data={overview.stages.READY} icon={CheckCircle} color="border-yellow-500 bg-yellow-50" />
              <PipelineStage stage="Submitted" data={overview.stages.SUBMITTED} icon={Clock} color="border-purple-500 bg-purple-50" />
              <PipelineStage stage="Paid" data={overview.stages.PAID} icon={CheckCircle} color="border-green-500 bg-green-50" />
              <PipelineStage stage="Part Paid" data={overview.stages.PART_PAID} icon={AlertTriangle} color="border-orange-500 bg-orange-50" />
              <PipelineStage stage="Rejected" data={overview.stages.REJECTED} icon={XCircle} color="border-red-500 bg-red-50" isLast />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Payment Rate */}
        {paymentTracking && (
          <Card data-testid="payment-rate-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Payment Rate</p>
                  <p className="text-3xl font-bold mt-1">{paymentTracking.payment_rate}%</p>
                  <p className="text-sm text-gray-500">Last 90 days</p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <Progress value={paymentTracking.payment_rate} className="mt-4" />
            </CardContent>
          </Card>
        )}

        {/* Avg Days to Payment */}
        {paymentTracking && (
          <Card data-testid="avg-payment-days-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Avg. Days to Payment</p>
                  <p className="text-3xl font-bold mt-1">{paymentTracking.average_days_to_payment}</p>
                  <p className="text-sm text-gray-500">days</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <CalendarClock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Payment */}
        {paymentTracking && (
          <Card data-testid="pending-payment-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Pending Payment</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(paymentTracking.total_pending)}</p>
                  <p className="text-sm text-gray-500">awaiting</p>
                </div>
                <div className="p-3 rounded-full bg-yellow-100">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* At Risk Participants */}
        {budgetAlerts && (
          <Card data-testid="at-risk-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Funding At Risk</p>
                  <p className="text-3xl font-bold mt-1">{budgetAlerts.summary.total_at_risk}</p>
                  <p className="text-sm text-gray-500">participants</p>
                </div>
                <div className={`p-3 rounded-full ${budgetAlerts.summary.total_at_risk > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${budgetAlerts.summary.total_at_risk > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs for detailed views */}
      <Tabs defaultValue="suggestions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Suggestions
          </TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging">
            <BarChart3 className="w-4 h-4 mr-2" />
            Claim Aging
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertCircle className="w-4 h-4 mr-2" />
            Budget Alerts
          </TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">
            <PieChart className="w-4 h-4 mr-2" />
            Reconciliation
          </TabsTrigger>
        </TabsList>

        {/* AI Suggestions Tab */}
        <TabsContent value="suggestions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Auto-Claim Suggestions
              </CardTitle>
              <CardDescription>
                AI-powered recommendations for optimal claim generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {suggestions?.suggestions?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p>No pending claims to generate</p>
                  <p className="text-sm">All eligible timesheets have been claimed</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg mb-4">
                    <div>
                      <span className="font-medium">{suggestions?.summary?.total_suggestions || 0} suggestions</span>
                      <span className="text-gray-500 ml-2">
                        totaling {formatCurrency(suggestions?.summary?.total_suggested_value)}
                      </span>
                    </div>
                    <Badge variant="secondary">
                      {suggestions?.summary?.high_priority_count || 0} high priority
                    </Badge>
                  </div>
                  
                  {suggestions?.suggestions?.map((suggestion, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      data-testid={`suggestion-${suggestion.participant_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{suggestion.participant_name}</p>
                          <Badge 
                            variant={suggestion.priority === 'HIGH' ? 'destructive' : suggestion.priority === 'MEDIUM' ? 'default' : 'secondary'}
                          >
                            {suggestion.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {suggestion.eligible_timesheets} timesheets &bull; {suggestion.date_range.from} to {suggestion.date_range.to}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{suggestion.recommendation}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(suggestion.estimated_value)}</p>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleQuickGenerate(suggestion.participant_id)}
                          data-testid={`generate-btn-${suggestion.participant_id}`}
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Generate
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Claim Aging Tab */}
        <TabsContent value="aging">
          <Card>
            <CardHeader>
              <CardTitle>Claim Aging Analysis</CardTitle>
              <CardDescription>
                How long claims have been in SUBMITTED status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {aging && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {aging.buckets?.map((bucket, i) => (
                      <div 
                        key={i}
                        className={`p-4 rounded-lg border ${
                          bucket.bucket.includes('90+') ? 'border-red-500 bg-red-50' :
                          bucket.bucket.includes('61-90') ? 'border-orange-500 bg-orange-50' :
                          bucket.bucket.includes('31-60') ? 'border-yellow-500 bg-yellow-50' :
                          'border-green-500 bg-green-50'
                        }`}
                        data-testid={`aging-bucket-${i}`}
                      >
                        <p className="text-sm font-medium text-gray-600">{bucket.bucket}</p>
                        <p className="text-2xl font-bold mt-1">{bucket.count}</p>
                        <p className="text-sm text-gray-500">{formatCurrency(bucket.total_value)}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Average Age</p>
                      <p className="text-xl font-bold">{aging.average_age_days} days</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Claims</p>
                      <p className="text-xl font-bold">{aging.total_claims}</p>
                    </div>
                    {aging.oldest_claim && (
                      <div>
                        <p className="text-sm text-gray-500">Oldest Claim</p>
                        <p className="text-xl font-bold">{aging.oldest_claim.claim_no}</p>
                        <p className="text-xs text-gray-400">{aging.oldest_claim.days} days old</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget Alerts Tab */}
        <TabsContent value="alerts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Overspent & Critical */}
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  Funding Critical
                </CardTitle>
                <CardDescription>
                  {(budgetAlerts?.alerts?.overspent?.length || 0) + (budgetAlerts?.alerts?.critical?.length || 0)} participants need attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {budgetAlerts?.alerts?.overspent?.map((alert, i) => (
                    <div key={`overspent-${i}`} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{alert.participant_name}</p>
                        <p className="text-xs text-red-600">OVERSPENT - {alert.utilisation_percent}% used</p>
                      </div>
                      <Badge variant="destructive">Overspent</Badge>
                    </div>
                  ))}
                  {budgetAlerts?.alerts?.critical?.map((alert, i) => (
                    <div key={`critical-${i}`} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{alert.participant_name}</p>
                        <p className="text-xs text-orange-600">{alert.utilisation_percent}% used - {formatCurrency(alert.available_budget)} remaining</p>
                      </div>
                      <Badge className="bg-orange-500">Critical</Badge>
                    </div>
                  ))}
                  {(!budgetAlerts?.alerts?.overspent?.length && !budgetAlerts?.alerts?.critical?.length) && (
                    <p className="text-center text-gray-500 py-4">No critical funding alerts</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Expiring Plans */}
            <Card className="border-yellow-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
                  <CalendarClock className="w-5 h-5" />
                  Expiring Plans
                </CardTitle>
                <CardDescription>
                  {budgetAlerts?.alerts?.expiring_plans?.length || 0} plans expiring within 30 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {budgetAlerts?.alerts?.expiring_plans?.map((alert, i) => (
                    <div key={`expiring-${i}`} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{alert.participant_name}</p>
                        <p className="text-xs text-yellow-600">Expires: {alert.plan_end_date}</p>
                      </div>
                      <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                        {alert.days_until_expiry} days
                      </Badge>
                    </div>
                  ))}
                  {!budgetAlerts?.alerts?.expiring_plans?.length && (
                    <p className="text-center text-gray-500 py-4">No plans expiring soon</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Warning */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-yellow-500" />
                  Funding Warnings
                </CardTitle>
                <CardDescription>
                  {budgetAlerts?.alerts?.warning?.length || 0} participants at 80-90% utilisation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {budgetAlerts?.alerts?.warning?.slice(0, 5).map((alert, i) => (
                    <div key={`warning-${i}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{alert.participant_name}</p>
                        <p className="text-xs text-gray-500">{alert.utilisation_percent}% used - {formatCurrency(alert.available_budget)} remaining</p>
                      </div>
                      <Badge variant="outline">{alert.days_remaining}d left</Badge>
                    </div>
                  ))}
                  {!budgetAlerts?.alerts?.warning?.length && (
                    <p className="text-center text-gray-500 py-4">No funding warnings</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Underspend */}
            <Card className="border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                  <TrendingDown className="w-5 h-5" />
                  Underspend Alert
                </CardTitle>
                <CardDescription>
                  {budgetAlerts?.alerts?.underspend?.length || 0} participants with low utilisation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {budgetAlerts?.alerts?.underspend?.slice(0, 5).map((alert, i) => (
                    <div key={`underspend-${i}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{alert.participant_name}</p>
                        <p className="text-xs text-blue-600">{alert.recommendation}</p>
                      </div>
                      <Badge variant="secondary">{alert.utilisation_percent}%</Badge>
                    </div>
                  ))}
                  {!budgetAlerts?.alerts?.underspend?.length && (
                    <p className="text-center text-gray-500 py-4">No underspend alerts</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation">
          <Card>
            <CardHeader>
              <CardTitle>Reconciliation Summary</CardTitle>
              <CardDescription>Last 90 days claim outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              {reconciliation && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Claims Reconciled</p>
                      <p className="text-2xl font-bold">{reconciliation.total_claims_reconciled}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600">Paid Items</p>
                      <p className="text-2xl font-bold text-green-700">{reconciliation.paid_items}</p>
                      <p className="text-sm text-green-600">{formatCurrency(reconciliation.paid_value)}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600">Rejected Items</p>
                      <p className="text-2xl font-bold text-red-700">{reconciliation.rejected_items}</p>
                      <p className="text-sm text-red-600">{formatCurrency(reconciliation.rejected_value)}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">Total Line Items</p>
                      <p className="text-2xl font-bold">{reconciliation.total_line_items}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-600">Success Rate</p>
                      <p className="text-2xl font-bold text-purple-700">{reconciliation.success_rate}%</p>
                    </div>
                  </div>
                  
                  {reconciliation.top_rejection_reasons?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Top Rejection Reasons</h4>
                      <div className="space-y-2">
                        {reconciliation.top_rejection_reasons.map((reason, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <span className="text-sm">{reason.reason}</span>
                            <Badge variant="outline">{reason.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancePipelinePage;
